import { type RefrigerationUnit } from '@/data/schemas';
import type { RefrigerationUnitTechnicalInput } from '@/data/repos/refrigerationUnitsRepo';
import {
  ACCESSIBILITY_VALUES,
  LEVEL_INSTRUMENTATION_VALUES,
  LOCATION_VALUES,
  MATERIAL_VALUES,
  ORIENTATION_VALUES,
  SIGNAGE_VALUES,
  VVF_DOCS_VALUES,
  parseNumberList,
  sanitizeEnumValue,
  toOptionalInt,
  toOptionalNumber,
  trim,
  type RefrigerationUnitFormValues,
} from './shared';

export const buildDischargeDefaults = (): Pick<
  RefrigerationUnitFormValues,
  | 'valvesReliefPressures'
  | 'valvesReliefPeriodicity'
  | 'valvesReliefNotes'
  | 'valvesIsolationPresent'
  | 'valvesIsolationDiverter'
  | 'valvesIsolationNotes'
  | 'vsReliefCollectorsCount'
  | 'vsReliefSizing'
  | 'abatementTankPresent'
  | 'abatementVolumeMc'
  | 'abatementMinWaterMc'
  | 'abatementMinFreeMc'
  | 'abatementLocation'
  | 'abatementSignage'
  | 'abatementAccessibility'
  | 'abatementMaterial'
  | 'abatementDimensions'
  | 'abatementOrientation'
  | 'abatementLevelInstrumentationPresent'
  | 'abatementLevelInstrumentationType'
  | 'abatementContinuousPhMonitoring'
  | 'abatementEmergencyPreferredLine'
  | 'abatementEmergencyGenerator'
  | 'abatementEmergencyUps'
  | 'vvfDocs'
  | 'vvfDocsLocation'
> => ({
  valvesReliefPressures: '',
  valvesReliefPeriodicity: null,
  valvesReliefNotes: '',
  valvesIsolationPresent: false,
  valvesIsolationDiverter: false,
  valvesIsolationNotes: '',
  vsReliefCollectorsCount: null,
  vsReliefSizing: '',
  abatementTankPresent: false,
  abatementVolumeMc: null,
  abatementMinWaterMc: null,
  abatementMinFreeMc: null,
  abatementLocation: '',
  abatementSignage: '',
  abatementAccessibility: '',
  abatementMaterial: '',
  abatementDimensions: '',
  abatementOrientation: '',
  abatementLevelInstrumentationPresent: false,
  abatementLevelInstrumentationType: '',
  abatementContinuousPhMonitoring: false,
  abatementEmergencyPreferredLine: false,
  abatementEmergencyGenerator: false,
  abatementEmergencyUps: false,
  vvfDocs: '',
  vvfDocsLocation: '',
});

export const buildDischargeValues = (
  refrigerationUnit: RefrigerationUnit
): Pick<
  RefrigerationUnitFormValues,
  | 'valvesReliefPressures'
  | 'valvesReliefPeriodicity'
  | 'valvesReliefNotes'
  | 'valvesIsolationPresent'
  | 'valvesIsolationDiverter'
  | 'valvesIsolationNotes'
  | 'vsReliefCollectorsCount'
  | 'vsReliefSizing'
  | 'abatementTankPresent'
  | 'abatementVolumeMc'
  | 'abatementMinWaterMc'
  | 'abatementMinFreeMc'
  | 'abatementLocation'
  | 'abatementSignage'
  | 'abatementAccessibility'
  | 'abatementMaterial'
  | 'abatementDimensions'
  | 'abatementOrientation'
  | 'abatementLevelInstrumentationPresent'
  | 'abatementLevelInstrumentationType'
  | 'abatementContinuousPhMonitoring'
  | 'abatementEmergencyPreferredLine'
  | 'abatementEmergencyGenerator'
  | 'abatementEmergencyUps'
  | 'vvfDocs'
  | 'vvfDocsLocation'
> => {
  const safety = refrigerationUnit.technical?.safety;
  const valves = safety?.valves;

  const abatementTankHasDetails =
    safety?.abatementTank?.volumeMc !== undefined ||
    safety?.abatementTank?.minWaterMc !== undefined ||
    safety?.abatementTank?.minFreeMc !== undefined ||
    Boolean(safety?.abatementTank?.location) ||
    Boolean(safety?.abatementTank?.signage) ||
    Boolean(safety?.abatementTank?.accessibility) ||
    Boolean(safety?.abatementTank?.material) ||
    Boolean(safety?.abatementTank?.dimensions) ||
    Boolean(safety?.abatementTank?.orientation) ||
    safety?.abatementTank?.levelInstrumentation?.present !== undefined ||
    Boolean(safety?.abatementTank?.levelInstrumentation?.type) ||
    safety?.abatementTank?.continuousPhMonitoring !== undefined ||
    safety?.abatementTank?.emergencySupply?.preferredLine !== undefined ||
    safety?.abatementTank?.emergencySupply?.generator !== undefined ||
    safety?.abatementTank?.emergencySupply?.ups !== undefined;

  return {
    valvesReliefPressures: (valves?.reliefSetPressuresBar ?? [])
      .map((value) => value.toString())
      .join(', '),
    valvesReliefPeriodicity: valves?.reliefPeriodicityYears ?? null,
    valvesReliefNotes: valves?.reliefNotes ?? '',
    valvesIsolationPresent: valves?.isolationPresent ?? false,
    valvesIsolationDiverter: valves?.isolationDiverter ?? false,
    valvesIsolationNotes: valves?.isolationNotes ?? '',
    vsReliefCollectorsCount: safety?.vsRelief?.collectorsCount ?? null,
    vsReliefSizing: safety?.vsRelief?.sizing ?? '',
    abatementTankPresent: safety?.abatementTank?.present ?? abatementTankHasDetails,
    abatementVolumeMc: safety?.abatementTank?.volumeMc ?? null,
    abatementMinWaterMc: safety?.abatementTank?.minWaterMc ?? null,
    abatementMinFreeMc: safety?.abatementTank?.minFreeMc ?? null,
    abatementLocation: safety?.abatementTank?.location ?? '',
    abatementSignage: safety?.abatementTank?.signage ?? '',
    abatementAccessibility: safety?.abatementTank?.accessibility ?? '',
    abatementMaterial: safety?.abatementTank?.material ?? '',
    abatementDimensions: safety?.abatementTank?.dimensions ?? '',
    abatementOrientation: safety?.abatementTank?.orientation ?? '',
    abatementLevelInstrumentationPresent:
      safety?.abatementTank?.levelInstrumentation?.present ?? false,
    abatementLevelInstrumentationType: safety?.abatementTank?.levelInstrumentation?.type ?? '',
    abatementContinuousPhMonitoring: safety?.abatementTank?.continuousPhMonitoring ?? false,
    abatementEmergencyPreferredLine: safety?.abatementTank?.emergencySupply?.preferredLine ?? false,
    abatementEmergencyGenerator: safety?.abatementTank?.emergencySupply?.generator ?? false,
    abatementEmergencyUps: safety?.abatementTank?.emergencySupply?.ups ?? false,
    vvfDocs: safety?.vvfDocs ?? '',
    vvfDocsLocation: safety?.vvfDocsLocation ?? '',
  };
};

export const dischargeToInput = (
  values: RefrigerationUnitFormValues
): Pick<
  NonNullable<RefrigerationUnitTechnicalInput['safety']>,
  'valves' | 'vsRelief' | 'abatementTank' | 'vvfDocs' | 'vvfDocsLocation'
> => {
  const valves =
    values.valvesIsolationPresent ||
    values.valvesIsolationDiverter ||
    trim(values.valvesIsolationNotes) ||
    parseNumberList(values.valvesReliefPressures)?.length ||
    toOptionalNumber(values.valvesReliefPeriodicity ?? undefined) !== undefined ||
    trim(values.valvesReliefNotes)
      ? {
          reliefSetPressuresBar: parseNumberList(values.valvesReliefPressures),
          reliefPeriodicityYears: toOptionalNumber(values.valvesReliefPeriodicity ?? undefined),
          reliefNotes: trim(values.valvesReliefNotes),
          isolationPresent: values.valvesIsolationPresent || undefined,
          isolationDiverter: values.valvesIsolationDiverter || undefined,
          isolationNotes: trim(values.valvesIsolationNotes),
        }
      : undefined;

  const vsRelief =
    toOptionalInt(values.vsReliefCollectorsCount ?? undefined) !== undefined ||
    trim(values.vsReliefSizing)
      ? {
          collectorsCount: toOptionalInt(values.vsReliefCollectorsCount ?? undefined),
          sizing: trim(values.vsReliefSizing),
        }
      : undefined;

  const abatementLocation = sanitizeEnumValue(values.abatementLocation ?? '', LOCATION_VALUES);
  const abatementSignage = sanitizeEnumValue(values.abatementSignage ?? '', SIGNAGE_VALUES);
  const abatementAccessibility = sanitizeEnumValue(
    values.abatementAccessibility ?? '',
    ACCESSIBILITY_VALUES
  );
  const abatementMaterial = sanitizeEnumValue(values.abatementMaterial ?? '', MATERIAL_VALUES);
  const abatementOrientation = sanitizeEnumValue(
    values.abatementOrientation ?? '',
    ORIENTATION_VALUES
  );
  const instrumentationType = sanitizeEnumValue(
    values.abatementLevelInstrumentationType ?? '',
    LEVEL_INSTRUMENTATION_VALUES
  );
  const levelInstrumentation =
    values.abatementLevelInstrumentationPresent || instrumentationType
      ? {
          present: values.abatementLevelInstrumentationPresent,
          type: instrumentationType,
        }
      : undefined;
  const abatementEmergencySupply =
    values.abatementEmergencyPreferredLine ||
    values.abatementEmergencyGenerator ||
    values.abatementEmergencyUps
      ? {
          preferredLine: values.abatementEmergencyPreferredLine,
          generator: values.abatementEmergencyGenerator,
          ups: values.abatementEmergencyUps,
        }
      : undefined;
  const abatementTank = values.abatementTankPresent
    ? {
        present: true,
        volumeMc: toOptionalNumber(values.abatementVolumeMc ?? undefined),
        minWaterMc: toOptionalNumber(values.abatementMinWaterMc ?? undefined),
        minFreeMc: toOptionalNumber(values.abatementMinFreeMc ?? undefined),
        location: abatementLocation,
        signage: abatementSignage,
        accessibility: abatementAccessibility,
        material: abatementMaterial,
        dimensions: trim(values.abatementDimensions),
        orientation: abatementOrientation,
        levelInstrumentation,
        continuousPhMonitoring: values.abatementContinuousPhMonitoring,
        emergencySupply: abatementEmergencySupply,
      }
    : { present: false };

  const vvfDocs = sanitizeEnumValue(values.vvfDocs ?? '', VVF_DOCS_VALUES);
  const vvfDocsLocation = trim(values.vvfDocsLocation);

  return {
    valves,
    vsRelief,
    abatementTank,
    vvfDocs,
    vvfDocsLocation,
  };
};
